// Schwebendes Coach-Fenster über die Document-Picture-in-Picture-API.
//
// Ersetzt das frameless Always-on-Top-Fenster der Electron-Version: Chrome und Edge
// können ein echtes kleines Fenster öffnen, das über allen anderen Programmen bleibt –
// also auch über Teams im Vollbild. Ist die API nicht verfügbar, zeigt die Oberfläche
// den Coach einfach in der Seite an; die Aufnahme ist davon nicht betroffen.
import { useCallback, useEffect, useState } from 'react';
import { formatDuration } from './format';

// Das PiP-Fenster erbt kein CSS vom Hauptfenster – die Stylesheets müssen kopiert werden.
function copyStyles(target) {
  for (const sheet of document.styleSheets) {
    try {
      const css = [...sheet.cssRules].map((r) => r.cssText).join('');
      const style = target.document.createElement('style');
      style.textContent = css;
      target.document.head.appendChild(style);
    } catch {
      // Stylesheets von fremden Domains lassen sich nicht auslesen. Im Build gibt es
      // die nicht – als Rückfalllösung das <link> übernehmen.
      if (sheet.href) {
        const link = target.document.createElement('link');
        link.rel = 'stylesheet';
        link.href = sheet.href;
        target.document.head.appendChild(link);
      }
    }
  }
}

// Fenstergrößen für den zugeklappten und den aufgeklappten Zustand.
// Zugeklappt bleibt nur die Leiste mit Timer und Steuerung sichtbar.
export const PIP_SIZE = {
  collapsed: { width: 320, height: 72 },
  expanded: { width: 340, height: 260 },
};

export function useCoachPip() {
  const [pipWindow, setPipWindow] = useState(null);
  const supported = typeof window !== 'undefined' && 'documentPictureInPicture' in window;

  // Muss direkt aus einem Klick heraus aufgerufen werden – die Browser verlangen
  // eine frische Nutzerinteraktion.
  const open = useCallback(async () => {
    if (!supported || pipWindow) return null;
    const win = await window.documentPictureInPicture.requestWindow(PIP_SIZE.expanded);
    copyStyles(win);
    win.document.body.classList.add('pip-body');
    win.addEventListener('pagehide', () => setPipWindow(null));
    setPipWindow(win);
    return win;
  }, [supported, pipWindow]);

  const close = useCallback(() => {
    if (pipWindow) pipWindow.close();
    setPipWindow(null);
  }, [pipWindow]);

  // Fenster nicht offen lassen, wenn die Seite verschwindet
  useEffect(() => () => {
    if (pipWindow) pipWindow.close();
  }, [pipWindow]);

  return { pipWindow, open, close, supported };
}

// Inhalt des schwebenden Fensters: Timer, Steuerung und der aktuelle Coach-Hinweis.
//
// Zuklappen verkleinert das Fenster wirklich, statt den Inhalt nur auszublenden –
// sonst bliebe ein großer leerer Rahmen über allen anderen Programmen stehen.
// resizeTo() ist für PiP-Fenster ab Chrome 121 erlaubt, verlangt aber eine
// Nutzerinteraktion; der Klick auf den Knopf hier ist genau das.
export function CoachOverlay({ state, coach, onCommand, pipWindow }) {
  const [expanded, setExpanded] = useState(true);
  const paused = state.status === 'paused';

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    const size = next ? PIP_SIZE.expanded : PIP_SIZE.collapsed;
    try {
      if (pipWindow) pipWindow.resizeTo(size.width, size.height);
    } catch {
      // Ältere Chrome-Versionen erlauben das Verkleinern nicht. Dann bleibt das
      // Fenster groß, der Coach-Text verschwindet aber trotzdem.
    }
  };

  return (
    <div className="overlay-root">
      <div className="overlay">
        <span className={`dot ${paused ? 'paused' : ''}`} />
        <span className="time">{formatDuration(state.elapsed)}</span>
        <span className="label">{paused ? 'Pausiert' : 'REC'}</span>
        <div className="buttons">
          <button
            className={`coach-toggle ${expanded ? 'on' : ''}`}
            title={expanded ? 'Coach ausblenden' : 'Coach einblenden'}
            onClick={toggleExpanded}
          >
            ✦
          </button>
          <button
            title={paused ? 'Fortsetzen' : 'Pause'}
            onClick={() => onCommand(paused ? 'resume' : 'pause')}
          >
            {paused ? '▶' : '⏸'}
          </button>
          <button className="stop" title="Stopp" onClick={() => onCommand('stop')}>
            ⏹
          </button>
        </div>
      </div>

      {expanded && (
        <div className="coach-card">
          <div className="coach-card-head">KI-Live-Coach</div>
          <div className="coach-card-text">
            {coach ? coach.tip : 'Höre zu … erste Empfehlung folgt nach den ersten Gesprächsminuten.'}
          </div>
        </div>
      )}
    </div>
  );
}
